import { useState, useCallback } from "react";
import accountService from "../services/api/accountService";

/**
 * Hook para gestión de cuentas con manejo de estado y operaciones CRUD
 * @returns {Object} Estado y métodos para gestión de cuentas
 */
export const useAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMockData, setIsMockData] = useState(false);

  const loadAccounts = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await accountService.getAccounts(params);

      let accountsData = [];
      let mockDataFlag = false;

      if (response && typeof response === "object") {
        if (response.isMockData) {
          mockDataFlag = true;
        }

        if (response.data && Array.isArray(response.data)) {
          accountsData = response.data;
        } else if (Array.isArray(response)) {
          accountsData = response;
        } else if (response.success && response.data) {
          accountsData = Array.isArray(response.data)
            ? response.data
            : [response.data];
        }
      }

      setAccounts(accountsData);
      setIsMockData(mockDataFlag);
    } catch (err) {
      const errorMessage =
        err.context || err.message || "Failed to load accounts";
      setError(errorMessage);
      setAccounts([]);
      setIsMockData(false);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const createAccount = useCallback(async (accountData) => {
    setLoading(true);
    setError(null);

    try {
      const newAccount = await accountService.createAccount(accountData);
      setAccounts((prev) => [...prev, newAccount]);
      return newAccount;
    } catch (err) {
      const errorMessage =
        err.context || err.message || "Failed to create account";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAccount = useCallback(async (id, accountData) => {
    setLoading(true);
    setError(null);

    try {
      const updatedAccount = await accountService.updateAccount(
        id,
        accountData
      );
      setAccounts((prev) =>
        prev.map((account) =>
          account.AccountId === id ? updatedAccount : account
        )
      );
      return updatedAccount;
    } catch (err) {
      const errorMessage =
        err.context || err.message || "Failed to update account";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAccount = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      await accountService.deleteAccount(id);
      setAccounts((prev) => prev.filter((account) => account.AccountId !== id));
    } catch (err) {
      const errorMessage =
        err.context || err.message || "Failed to delete account";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const retry = useCallback(() => {
    if (accounts.length === 0) {
      loadAccounts();
    }
  }, [accounts.length, loadAccounts]);

  return {
    accounts,
    loading,
    error,
    isMockData,
    loadAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    clearError,
    retry,
  };
};
